///
/// EarthBrowser
///
/// Created by Matt Giger on 9/24/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Per-route document metadata (title, description, canonical, Open Graph,
/// Twitter card, robots). The static defaults live in index.html, which is
/// what crawlers and social scrapers see before JavaScript runs; the tags
/// there carry data-rh="true" so react-helmet-async takes ownership and
/// replaces them in place when a route supplies different values, rather
/// than appending duplicates. Every route must render this once.
///

import { Helmet } from 'react-helmet-async'

export const SITE_URL = 'https://earthbrowser.com'
export const SITE_NAME = 'EarthBrowser'

// Keep identical to the static tags in index.html
export const DEFAULT_TITLE = 'EarthBrowser – Live 3D Globe with Weather Radar & Satellites'
export const DEFAULT_DESCRIPTION = 'A live 3D globe of Earth with real-time weather radar, hurricanes, wildfires, earthquakes, satellite tracking and aurora forecasts. Free in your browser.'
const DEFAULT_IMAGE = `${SITE_URL}/images/preview.jpg`
const DEFAULT_IMAGE_ALT = 'EarthBrowser 3D globe over the Pacific and North America with live radar, hurricane tracks, weather fronts and isobars'

interface PageMetaProps {
	/** Page title, suffixed with the site name (aim for <= 60 chars total). Omit for the home-page default */
	title?: string
	/** Search snippet, aim for <= 155 chars */
	description?: string
	/** Route path for the canonical URL and og:url, e.g. "/pricing" */
	path: string
	/** Keep account, transactional and error pages out of search results */
	noindex?: boolean
}

export function PageMeta({ title, description = DEFAULT_DESCRIPTION, path, noindex = false }: PageMetaProps) {
	const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE
	const url = `${SITE_URL}${path}`

	return (
		<Helmet>
			<title>{fullTitle}</title>
			<meta name="description" content={description} />
			<meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
			<link rel="canonical" href={url} />

			<meta property="og:type" content="website" />
			<meta property="og:site_name" content={SITE_NAME} />
			<meta property="og:locale" content="en_US" />
			<meta property="og:url" content={url} />
			<meta property="og:title" content={fullTitle} />
			<meta property="og:description" content={description} />
			<meta property="og:image" content={DEFAULT_IMAGE} />
			<meta property="og:image:width" content="1200" />
			<meta property="og:image:height" content="630" />
			<meta property="og:image:alt" content={DEFAULT_IMAGE_ALT} />

			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={fullTitle} />
			<meta name="twitter:description" content={description} />
			<meta name="twitter:image" content={DEFAULT_IMAGE} />
			<meta name="twitter:image:alt" content={DEFAULT_IMAGE_ALT} />
		</Helmet>
	)
}
